// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateTime } from '@loa/theme';
import { Button, Checkbox, Skeleton, SearchBar, AppIcon, TablePagination, TagChipGroup, TagFilterSelect } from '@loa/ui-components';
import ReassignRelationshipModal from '../components/ReassignRelationshipModal';
import { ViewToggle } from '../components/ViewToggle';
import { BulkSelectionBar } from '../components/BulkSelectionBar';
import { AdvisorSelector, useAdvisorSelection } from '../components/AdvisorSelector';
import { useAdminViewToggle } from '../hooks/useAdminViewToggle';
import { useAdvisor } from '../contexts/AdvisorContext';
import { useTags } from '../contexts/TagsContext';
import { useEntityTags } from '../hooks/useEntityTags';
import { relationshipApi } from '../api/relationshipApi';
import type { RelationshipListFilters } from '../api/relationshipApi';
import { supportApi } from '../api/supportApi';
import type { Relationship, PageResponse, RelationshipHealthStats } from '../types/relationship';
import type { RelationshipDerivedStatus } from '../types/client';
import { usePageContextRegister } from '../contexts/PageContextProvider';

// ============================================
// Status Badge Configuration (Figma)
// ============================================

interface StatusConfig {
  label: string;
  textClass: string;
  bgClass: string;
  icon: React.ReactNode;
}

const statusConfigs: Record<string, StatusConfig> = {
  INCOMPLETE: {
    label: 'Invite not sent',
    bgClass: 'bg-error-bg',
    textClass: 'text-error-text',
    icon: <AppIcon name="invite not sent" size={14} />,
  },
  INVITE_LINK_SENT: {
    label: 'Invite link sent',
    bgClass: 'bg-warning-bg',
    textClass: 'text-warning-text',
    icon: <AppIcon name="envelope" size={14} />,
  },
  NEEDS_ATTENTION: {
    label: 'Needs Attention',
    bgClass: 'bg-error-bg',
    textClass: 'text-error-text',
    icon: <AppIcon name="refresh" size={14} />,
  },
  ACTIVE: {
    label: 'LoA Invite sent',
    bgClass: 'bg-warning-bg',
    textClass: 'text-warning-text',
    icon: <AppIcon name="envelope" size={14} />,
  },
  PARTIALLY_SIGNED: {
    label: 'LoA partially signed',
    bgClass: 'bg-warning-bg',
    textClass: 'text-warning-text',
    icon: <AppIcon name="clock" size={14} />,
  },
  PENDING_PROVIDER: {
    label: 'Pending provider',
    bgClass: 'bg-warning-bg',
    textClass: 'text-warning-text',
    icon: <AppIcon name="clock" size={14} />,
  },
  FULLY_SIGNED: {
    label: 'Pending provider',
    bgClass: 'bg-warning-bg',
    textClass: 'text-warning-text',
    icon: <AppIcon name="clock" size={14} />,
  },
  DATA_AVAILABLE: {
    label: 'Data available',
    bgClass: 'bg-success-bg',
    textClass: 'text-success-text',
    icon: <AppIcon name="checkbox" size={14} />,
  },
  REJECTED: {
    label: 'LoA rejected',
    bgClass: 'bg-error-bg',
    textClass: 'text-error-text',
    icon: <AppIcon name="rejected" size={14} />,
  },
  PROVIDER_REJECTED: {
    label: 'Provider rejected',
    bgClass: 'bg-error-bg',
    textClass: 'text-error-text',
    icon: <AppIcon name="rejected" size={14} />,
  },
  REFRESH_NEEDED: {
    label: 'Refresh needed',
    bgClass: 'bg-error-bg',
    textClass: 'text-error-text',
    icon: <AppIcon name="refresh" size={14} />,
  },
  ARCHIVED: {
    label: 'Archived',
    bgClass: 'bg-grey-06',
    textClass: 'text-black',
    icon: <AppIcon name="archive box" size={14} />,
  },
};

const defaultStatusConfig: StatusConfig = {
  label: 'Pending',
  bgClass: 'bg-warning-bg',
  textClass: 'text-warning-text',
  icon: <AppIcon name="clock" size={14} />,
};

function RelationshipStatusBadge({ status }: { status?: string }) {
  const config = (status && statusConfigs[status]) || defaultStatusConfig;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-body-03 font-medium leading-normal whitespace-nowrap overflow-hidden text-ellipsis ${config.bgClass} ${config.textClass}`}
    >
      {config.icon}
      {config.label}
    </span>
  );
}

// ============================================
// Helpers
// ============================================

// ============================================
// Map URL health parameter values to internal filter status values
// ============================================

const healthParamToFilterStatus: Record<string, string> = {
  'needs-attention': 'needs_attention',
  'incomplete': 'incomplete',
  'active': 'active',
  'partially-signed': 'partially_signed',
  'fully-signed': 'fully_signed',
  'archived': 'archived',
  // Independent banner flag filters
  'not-invited': 'flag:notInvited',
  'no-policies': 'flag:noPolicies',
  'data-available-new': 'flag:dataAvailableNew',
  'loa-rejected': 'flag:loaRejected',
};

// ============================================
// Main Component
// ============================================

export function RelationshipsPage() {
  usePageContextRegister({ pageType: 'RELATIONSHIPS_LIST' });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { advisor, loading: advisorLoading, error: advisorError, isSupport, isAdmin } = useAdvisor();
  const [relationships, setRelationships] = useState<PageResponse<Relationship> | null>(null);
  const [relationshipsLoading, setRelationshipsLoading] = useState(true);

  // Support mode state
  const { selectedAdvisorId, setSelectedAdvisorId } = useAdvisorSelection();

  // Initialize filter from URL query parameter
  const healthParam = searchParams.get('health');
  const initialFilterStatus = healthParam ? (healthParamToFilterStatus[healthParam] || 'all') : 'all';
  const [filterStatus, setFilterStatus] = useState<string>(initialFilterStatus);

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);

  // Local search query for filtering the table
  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [crmSearching, setCrmSearching] = useState(false);

  // State for health stats. The matching `statsLoading` boolean used to be
  // tracked alongside but the value was never read — drop the useState pair
  // and just fire the side-effect fetch.
  const [healthStats, setHealthStats] = useState<RelationshipHealthStats | null>(null);

  // State for reassignment modal
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState<Relationship | null>(null);

  // Admin view toggle hook
  const {
    currentView,
    setCurrentView,
    isAdminView,
    showToggle,
    isInitializing
  } = useAdminViewToggle({
    storageKey: 'relationships',
    syncWithUrl: true
  });

  // State for advisor filter (only used in admin view)
  const [advisorFilter, setAdvisorFilter] = useState<string>('all');

  // Tag filter (Tags PRD FR-13, ANY-of). `tags`/`featureEnabled` come from
  // the session-cached TagsProvider — the filter control renders nothing
  // when the feature is off for the tenant.
  const { tags: allTags, featureEnabled: tagsFeatureEnabled } = useTags();
  const [tagFilterIds, setTagFilterIds] = useState<string[]>([]);

  // State for bulk selection (admin users only)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Sort state for table columns
  type SortField = 'status' | 'name' | 'type';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Ref to track if URL health filter has been applied (to prevent reset from overwriting)
  const urlFilterApplied = useRef(false);

  // Capture the initial health param on first render (before any effects can clear it)
  const initialHealthParam = useRef<string | null>(null);
  if (initialHealthParam.current === null) {
    const urlParams = new URLSearchParams(globalThis.location.search);
    initialHealthParam.current = urlParams.get('health') || '';
  }

  // Build server-side filter params from current filter state
  const buildFilterParams = useCallback((): RelationshipListFilters => {
    const filters: RelationshipListFilters = {
      page: currentPage,
      size: pageSize,
    };

    if (isAdminView) filters.all = true;

    if (committedSearch) filters.search = committedSearch;

    if (filterStatus !== 'all') {
      if (filterStatus.startsWith('flag:')) {
        const flag = filterStatus.substring(5);
        if (flag === 'notInvited') filters.notInvited = true;
        else if (flag === 'noPolicies') filters.noPolicies = true;
        else if (flag === 'dataAvailableNew') filters.dataAvailable = true;
        else if (flag === 'loaRejected') filters.loaRejected = true;
      } else {
        filters.derivedStatus = filterStatus.toUpperCase();
      }
    }

    if (sortField) {
      const sortFieldMap: Record<string, string> = {
        status: 'cachedStatus',
        name: 'profileName',
        type: 'profileType',
      };
      filters.sortBy = sortFieldMap[sortField] || sortField;
      filters.sortDirection = sortDir;
    }

    if (tagFilterIds.length > 0) filters.tagIds = tagFilterIds;

    return filters;
  }, [currentPage, pageSize, isAdminView, committedSearch, filterStatus, sortField, sortDir, tagFilterIds]);

  const loadRelationships = useCallback(async () => {
    try {
      setRelationshipsLoading(true);

      // Support mode: Load profiles via support API
      if (isSupport) {
        const paginatedProfiles = selectedAdvisorId === 'all'
          ? await supportApi.getAllAssignedProfiles()
          : await supportApi.getAdvisorProfiles(selectedAdvisorId);
        setRelationships({
          content: paginatedProfiles.content.map((p) => ({
            id: p.id,
            profileName: p.profileName,
            profileType: p.profileType as 'SOLO' | 'JOINT' | 'GROUP' | undefined,
            crmRelationshipNumber: undefined,
            status: p.status as 'active' | 'inactive' | 'archived',
            derivedStatus: undefined,
            createdAt: p.createdAt,
            updatedAt: p.updatedAt,
            assignedToId: p.assignedToId,
            assignedToName: p.assignedToName,
            brokerageId: '',
            createdById: p.assignedToId,
            primaryClientId: null,
            relationshipType: (p.profileType as 'SOLO' | 'JOINT' | 'GROUP') || 'SOLO',
            createdBy: '',
            updatedBy: '',
          } as Relationship)),
          totalElements: paginatedProfiles.totalElements,
          totalPages: paginatedProfiles.totalPages,
          number: paginatedProfiles.number,
          size: paginatedProfiles.size,
        });
        setRelationshipsLoading(false);
        return;
      }

      // Admin/Advisor mode with server-side filters
      const data = await relationshipApi.getRelationships(buildFilterParams());
      setRelationships(data);
      setRelationshipsLoading(false);
    } catch (err) {
      console.error('Failed to load relationships:', err);
      setRelationshipsLoading(false);
    }
  }, [buildFilterParams, isSupport, selectedAdvisorId]);

  // Load health stats
  const loadStats = useCallback(async () => {
    try {
      const health = await relationshipApi.getRelationshipHealthStats().catch(() => null);
      setHealthStats(health);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    loadRelationships();
  }, [loadRelationships]);

  // Reload when selected advisor changes in support mode
  useEffect(() => {
    if (isSupport) {
      setCurrentPage(0);
    }
  }, [selectedAdvisorId, isSupport]);

  // Any tag filter change resets to page 0 so the user doesn't land on an
  // empty page after narrowing the result set.
  useEffect(() => {
    setCurrentPage(0);
  }, [tagFilterIds]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Apply initial URL health filter after component initialization is complete
  useEffect(() => {
    if (!isInitializing && !urlFilterApplied.current && initialHealthParam.current) {
      const mappedStatus = healthParamToFilterStatus[initialHealthParam.current];
      if (mappedStatus) {
        setFilterStatus(mappedStatus);
        urlFilterApplied.current = true;
      }
    }
  }, [isInitializing]);

  // Sync filter state with URL health parameter changes
  useEffect(() => {
    if (!urlFilterApplied.current) return;
    const healthParam = searchParams.get('health');
    if (healthParam) {
      const mappedStatus = healthParamToFilterStatus[healthParam];
      if (mappedStatus && mappedStatus !== filterStatus) {
        setFilterStatus(mappedStatus);
      }
    }
  }, [searchParams, filterStatus]);

  // Track the previous view to detect actual user-initiated view changes
  const previousView = useRef<string | null>(null);

  // Reset filters, pagination, and selection when view changes (user-initiated only)
  useEffect(() => {
    if (isInitializing) return;
    if (previousView.current === null) {
      previousView.current = currentView;
      return;
    }
    if (previousView.current === currentView) return;

    previousView.current = currentView;
    setCurrentPage(0);
    setFilterStatus('all');
    setAdvisorFilter('all');

    const currentParams = new URLSearchParams(globalThis.location.search);
    if (currentParams.has('health')) {
      currentParams.delete('health');
      setSearchParams(currentParams, { replace: true });
    }

    setSearchQuery('');
    setCommittedSearch('');
    setSelectedIds(new Set());
    setSelectAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, isInitializing]);

  const displayedRelationships = relationships?.content || [];

  // Batch tag decoration — ONE call per page of rows (never per-row), per
  // the cross-cloud DB latency rule (Tags PRD §10 NFR).
  const relationshipIds = displayedRelationships.map((r) => r.id);
  const { tagsByEntityId } = useEntityTags('RELATIONSHIP', relationshipIds);

  // Bulk selection handlers
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked && displayedRelationships.length > 0) {
      setSelectedIds(new Set(displayedRelationships.map(r => r.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRelationship = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
    setSelectAll(newSelected.size === displayedRelationships.length && displayedRelationships.length > 0);
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setSelectAll(false);
  };

  const handleBulkReassign = () => {
    const firstSelectedId = Array.from(selectedIds)[0];
    const relationship = displayedRelationships.find(r => r.id === firstSelectedId);
    if (relationship) {
      setSelectedRelationship(relationship);
      setShowReassignModal(true);
    }
  };

  const handleReassignSuccess = (updatedRelationship: Relationship) => {
    setRelationships(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        content: prev.content.map(r =>
          r.id === updatedRelationship.id ? updatedRelationship : r
        )
      };
    });
    setShowReassignModal(false);
    setSelectedRelationship(null);
  };

  const handleHealthFilterClick = (status: RelationshipDerivedStatus | 'all' | string) => {
    const statusMap: Record<string, { filter: string; urlParam: string }> = {
      'NEEDS_ATTENTION': { filter: 'needs_attention', urlParam: 'needs-attention' },
      'INCOMPLETE': { filter: 'incomplete', urlParam: 'incomplete' },
      'ACTIVE': { filter: 'active', urlParam: 'active' },
      'PARTIALLY_SIGNED': { filter: 'partially_signed', urlParam: 'partially-signed' },
      'FULLY_SIGNED': { filter: 'fully_signed', urlParam: 'fully-signed' },
      'ARCHIVED': { filter: 'archived', urlParam: 'archived' },
      // Independent banner flag filters
      'flag:notInvited': { filter: 'flag:notInvited', urlParam: 'not-invited' },
      'flag:noPolicies': { filter: 'flag:noPolicies', urlParam: 'no-policies' },
      'flag:dataAvailableNew': { filter: 'flag:dataAvailableNew', urlParam: 'data-available-new' },
      'flag:loaRejected': { filter: 'flag:loaRejected', urlParam: 'loa-rejected' },
    };

    const newParams = new URLSearchParams(globalThis.location.search);

    if (status === 'all') {
      setFilterStatus('all');
      newParams.delete('health');
    } else {
      const mapping = statusMap[status];
      setFilterStatus(mapping?.filter || 'all');
      if (mapping?.urlParam) {
        newParams.set('health', mapping.urlParam);
      } else {
        newParams.delete('health');
      }
    }

    setCurrentPage(0);
    setSearchParams(newParams, { replace: true });
  };

  // Search handler: commits search query for server-side filtering
  // Also attempts CRM number lookup for direct navigation
  const handleCrmSearch = useCallback(async () => {
    const trimmed = searchQuery.trim();
    setCommittedSearch(trimmed);
    setCurrentPage(0);
    if (!trimmed) return;
    try {
      setCrmSearching(true);
      const relationship = await relationshipApi.searchByCrmRelationshipNumber(trimmed);
      navigate(`/relationships/${relationship.id}`);
    } catch {
      // CRM search failed — server-side filter will handle it
    } finally {
      setCrmSearching(false);
    }
  }, [searchQuery, navigate]);

  // Loading state
  if (advisorLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-primary mx-auto mb-3" />
          <span className="text-body-03 text-gray-500 font-google">Loading your account...</span>
        </div>
      </div>
    );
  }

  if (advisorError) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <h3 className="text-red-700 font-semibold font-google mb-1">Error Loading Account</h3>
          <p className="text-red-600 text-body-03 font-google">{advisorError}</p>
        </div>
      </div>
    );
  }

  if (!advisor) {
    return (
      <div className="p-8">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h3 className="text-amber-700 font-semibold font-google mb-1">No Account Found</h3>
          <p className="text-amber-600 text-body-03 font-google">Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  // Default stats when API not yet implemented
  const defaultHealthStats: RelationshipHealthStats = {
    needsAttention: 0,
    incomplete: 0,
    active: relationships?.content.filter(r => r.status === 'active').length || 0,
    partiallySigned: 0,
    fullySigned: 0,
    archived: relationships?.content.filter(r => r.status === 'archived').length || 0,
    notInvited: 0,
    noPolicies: 0,
    dataAvailableNew: 0,
    loaRejected: 0,
  };

  const currentHealthStats = healthStats || defaultHealthStats;
  const totalRelationships = relationships?.totalElements || 0;
  const totalPages = relationships?.totalPages || 0;
  const currentPageNumber = relationships?.number || 0;

  return (
    <div className="pb-8 font-google">
      {/* Support Mode: Advisor Selector */}
      {isSupport && (
        <div className="mb-4">
          <AdvisorSelector
            value={selectedAdvisorId}
            onChange={setSelectedAdvisorId}
            disabled={relationshipsLoading}
          />
        </div>
      )}

      {/* View Toggle - Only shown for admin users (not support) */}
      {showToggle && !isSupport && (
        <div className="mb-4">
          <ViewToggle
            currentView={currentView}
            onViewChange={setCurrentView}
            disabled={isInitializing || relationshipsLoading}
          />
        </div>
      )}

      {/* Stats Cards Row */}
      {!isAdminView && (
        <div className="flex justify-between items-start py-6 px-4 bg-white rounded-2xl shadow-[0px_0px_10px_rgba(0,0,0,0.10)] my-lg mt-[24px mx-lg">
          {/* Total Relationships */}
          <div
            className="flex-1 px-4"
            onClick={() => handleHealthFilterClick('all')}
          >
            <div className="text-body-03 text-black font-normal leading-normal">Total Relationships</div>
            <div className="text-h4 font-medium text-success-text leading-tight mt-1">{totalRelationships}</div>
          </div>
          <div className="w-px bg-gray-200 self-stretch" />
          {/* Not invited */}
          <div
            className="flex-1 px-4 cursor-pointer"
            onClick={() => currentHealthStats.notInvited > 0 && handleHealthFilterClick('flag:notInvited')}
          >
            <div className="text-body-03 text-black font-normal leading-normal">Not invited</div>
            <div className="text-h4 font-medium text-error-text leading-tight mt-1">{currentHealthStats.notInvited}</div>
          </div>
          <div className="w-px bg-gray-200 self-stretch" />
          {/* No policies added */}
          <div
            className="flex-1 px-4 cursor-pointer"
            onClick={() => currentHealthStats.noPolicies > 0 && handleHealthFilterClick('flag:noPolicies')}
          >
            <div className="text-body-03 text-black font-normal leading-normal">No policies added</div>
            <div className="text-h4 font-medium text-error-text leading-tight mt-1">{currentHealthStats.noPolicies}</div>
          </div>
          <div className="w-px bg-gray-200 self-stretch" />
          {/* Data Available */}
          <div
            className="flex-1 px-4 cursor-pointer"
            onClick={() => currentHealthStats.dataAvailableNew > 0 && handleHealthFilterClick('flag:dataAvailableNew')}
          >
            <div className="text-body-03 text-black font-normal leading-normal">Data Available</div>
            <div className="text-h4 font-medium text-success-text leading-tight mt-1">{currentHealthStats.dataAvailableNew}</div>
          </div>
          <div className="w-px bg-gray-200 self-stretch" />
          {/* LOA Rejected */}
          <div
            className="flex-1 px-4 cursor-pointer"
            onClick={() => currentHealthStats.loaRejected > 0 && handleHealthFilterClick('flag:loaRejected')}
          >
            <div className="text-body-03 text-black font-normal leading-normal">LOA Rejected</div>
            <div className="text-h4 font-medium text-error-text leading-tight mt-1">{currentHealthStats.loaRejected}</div>
          </div>
        </div>
      )}

      {/* Main Relationships Card */}
      <div className="bg-white rounded-2xl shadow-[0px_0px_10px_rgba(0,0,0,0.1)] overflow-hidden mx-lg ">
        {/* Card Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="text-body-01 font-medium font-google text-black leading-normal">
            Relationships
          </div>
        </div>

        {/* Search + Filter + Action Buttons */}
        <div className="flex items-center gap-3 px-6 pb-5 justify-between">
          {/* Search Input */}
          <div className="flex-1">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={handleCrmSearch}
              placeholder="Search by relationship name, provider or policy type"
            />
          </div>

          {tagsFeatureEnabled && (
            <TagFilterSelect
              options={allTags.filter((t) => !t.archived).map((t) => ({ id: t.id, name: t.name, color: t.color }))}
              selectedIds={tagFilterIds}
              onChange={setTagFilterIds}
              data-testid="relationships-tag-filter"
            />
          )}

          {/* Add New Relationship Button */}
          <Button
            variant="filled"
            onClick={() => navigate('/relationships/new')}
            iconLeft={<AppIcon name="plus" size={14} />}
          >
            Add New Relationship
          </Button>
        </div>

        {/* Bulk Selection Bar - Only shown for admin users (not support) when items are selected */}
        {isAdmin && !isSupport && (
          <div className="px-6 pb-3">
            <BulkSelectionBar
              selectedCount={selectedIds.size}
              totalCount={displayedRelationships.length}
              isAllSelected={selectAll}
              onSelectAllChange={handleSelectAll}
              onClearSelection={handleClearSelection}
              onBulkReassign={handleBulkReassign}
              disabled={relationshipsLoading}
            />
          </div>
        )}

        {/* Table */}
        {relationshipsLoading || crmSearching ? (
          <div className="px-6 pb-6">
            <Skeleton variant="paragraph" lines={5} />
          </div>
        ) : displayedRelationships.length === 0 ? (
          <div className="mx-6 mb-6 rounded-xl bg-lilac-05 flex items-center justify-center py-8">
            <span className="text-black text-[15px]">
              {filterStatus !== 'all' || (isAdminView && advisorFilter !== 'all') || searchQuery.trim()
                ? 'No relationships match the current filters.'
                : isAdminView
                  ? 'No relationships exist in the brokerage.'
                  : 'Create your first relationship to get started.'}
            </span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#f6f4ff] font-medium">
                  {/* Checkbox column - admin only */}
                  {isAdmin && !isSupport && (
                    <th className="px-4 py-3 w-[40px]">
                      <Checkbox
                        checked={selectAll}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        aria-label="Select all relationships"
                        size="sm"
                      />
                    </th>
                  )}
                  {[
                    { label: 'Relationship Name', field: null, wide: true },
                    ...(isAdminView || isSupport ? [{ label: 'Assigned To', field: null, wide: false }] : []),
                    { label: 'Type', field: null, wide: false },
                    { label: 'CRM No.', field: null, wide: false },
                    { label: 'No. of policies', field: null, wide: false },
                    { label: 'No. of clients', field: null, wide: false },
                    { label: 'Last activity', field: null, wide: false },
                    { label: 'Status', field: 'status' as SortField, wide: false },
                    ...(tagsFeatureEnabled ? [{ label: 'Tags', field: null, wide: false }] : []),
                  ].map((col) => {
                    const isSortable = col.field !== null;
                    const isActive = isSortable && sortField === col.field;
                    return (
                      <th
                        key={col.label}
                        className={`px-4 py-3 text-left text-[13px] font-medium text-black font-google whitespace-nowrap ${col.wide ? 'min-w-[220px]' : ''} ${isSortable ? 'cursor-pointer select-none hover:bg-[#eee9fa]' : ''}`}
                        onClick={isSortable && col.field ? () => handleSort(col.field) : undefined}
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {isSortable && (
                            <span
                              className={`inline-flex transition-transform ${isActive && sortDir === 'asc' ? 'rotate-180' : ''}`}
                            >
                              <AppIcon name="chevron down" size={14} />
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayedRelationships.map((relationship) => {
                  const status = relationship.derivedStatus || relationship.status?.toUpperCase() || 'ACTIVE';
                  const typeName = relationship.profileType
                    ? relationship.profileType.charAt(0) + relationship.profileType.slice(1).toLowerCase()
                    : 'Solo';

                  return (
                    <tr
                      key={relationship.id}
                      className="border-t border-grey-06 hover:bg-page-bg transition-colors cursor-pointer"
                      onClick={() => navigate(`/relationships/${relationship.id}`)}
                    >
                      {/* Checkbox cell - admin only */}
                      {isAdmin && !isSupport && (
                        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(relationship.id)}
                            onChange={(e) => handleSelectRelationship(relationship.id, e.target.checked)}
                            aria-label={`Select ${relationship.profileName || 'relationship'}`}
                            size="sm"
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-body-03 font-google text-black">
                        {relationship.profileName || 'Unnamed Relationship'}
                      </td>
                      {/* Assigned To cell */}
                      {(isAdminView || isSupport) && (
                        <td className="px-6 py-4 text-body-03 font-google text-black">
                          {relationship.assignedToName || (
                            <span className="text-gray-400 italic">Unassigned</span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 text-body-03 font-google text-black">
                        {typeName}
                      </td>
                      <td className="px-6 py-4 text-body-03 font-google text-black">
                        {relationship.crmRelationshipNumber || (
                          <span className="text-gray-400">&mdash;</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-body-03 font-google text-black">
                        {relationship.policyCount ?? 0}
                      </td>
                      <td className="px-6 py-4 text-body-03 font-google text-black">
                        {relationship.clientCount ?? 0}
                      </td>
                      <td className="px-6 py-4 text-body-03 font-google text-black whitespace-nowrap">
                        {formatDateTime(relationship.updatedAt)}
                      </td>
                      <td className="px-6 py-4">
                        <RelationshipStatusBadge status={status} />
                      </td>
                      {tagsFeatureEnabled && (
                        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                          <TagChipGroup
                            tags={(tagsByEntityId[relationship.id] ?? []).map((t) => ({
                              id: t.id, name: t.name, color: t.color, archived: t.archived,
                            }))}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {relationships && totalPages > 0 && (
          <TablePagination
            config={{
              currentPage: currentPageNumber + 1,
              totalPages,
              itemsPerPage: pageSize,
              itemsPerPageOptions: [5, 10, 20, 50],
              onPageChange: (next) => handlePageChange(next - 1),
              onItemsPerPageChange: (size) => {
                setPageSize(size);
                setCurrentPage(0);
              },
            }}
          />
        )}
      </div>

      {/* Reassign Relationship Modal */}
      <ReassignRelationshipModal
        open={showReassignModal}
        relationship={selectedRelationship}
        currentAdvisorId={selectedRelationship?.assignedToId}
        onClose={() => {
          setShowReassignModal(false);
          setSelectedRelationship(null);
        }}
        onSuccess={handleReassignSuccess}
      />
    </div>
  );
}
